import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import { savePendingAction } from '../utils/pendingAction';
import ProductCard from '../components/product/ProductCard';
import CachedImage from '../components/CachedImage';
import { getImagesForColor, getColorTaggedImage } from '../utils/productImagesByColor';
import {
  getProductSellingPrice,
  getProductDiscountPercent,
  hasActiveProductDiscount,
} from '../utils/productPricing';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const preferredColorFromUrl = searchParams.get('color');

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColorName, setSelectedColorName] = useState('');
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [inWishlist, setInWishlist] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [showDelivery, setShowDelivery] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSizeChartModal, setShowSizeChartModal] = useState(false);
  const [sizeChart, setSizeChart] = useState(null);
  const [loadingSizeChart, setLoadingSizeChart] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    message: '',
    saveInfo: false,
  });
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    review_text: '',
    order_id: null,
    order_item_id: null,
    color: '',
    size: '',
  });
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [showCommentMenu, setShowCommentMenu] = useState(null);
  const [bestsellers, setBestsellers] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [mainImageLoading, setMainImageLoading] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);


  useEffect(() => {
    // Reset selections when product ID changes
    setSelectedColor(null);
    setSelectedColorName('');
    setSelectedSize(null);
    setQuantity(1);

    fetchProduct();
    fetchReviews();
    fetchBestsellers();
    fetchSimilarProducts();
    if (isAuthenticated) fetchUserOrders();
  }, [id, isAuthenticated]);

  // When landing from a color-specific listing card, re-apply preferred color
  useEffect(() => {
    setSelectedColor(null);
    setSelectedColorName('');
    setSelectedSize(null);
  }, [preferredColorFromUrl]);

  // Close comment menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCommentMenu && !event.target.closest('.comment-options-wrapper')) {
        setShowCommentMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCommentMenu]);

  const displayImages = useMemo(
    () => getImagesForColor(product?.images, selectedColor),
    [product?.images, selectedColor]
  );

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedColor]);

  useEffect(() => {
    if (displayImages.length === 0) return;
    setSelectedImageIndex((i) => Math.min(i, displayImages.length - 1));
  }, [displayImages.length]);

  // Show skeleton when selected image index changes until the new image loads (must be before any return)
  const didMountImageIndex = useRef(true);
  useEffect(() => {
    if (didMountImageIndex.current) {
      didMountImageIndex.current = false;
      return;
    }
    setMainImageLoading(true);
  }, [selectedImageIndex]);

  // Set default color and size when product loads (prefer ?color= from listing card)
  useEffect(() => {
    if (product?.variants && product.variants.length > 0 && selectedColor === null) {
      const urlColorId = preferredColorFromUrl != null ? Number(preferredColorFromUrl) : null;
      const urlColorVariant =
        urlColorId != null && Number.isFinite(urlColorId)
          ? product.variants.find((v) => Number(v.color_id) === urlColorId)
          : null;

      if (urlColorVariant) {
        setSelectedColor(urlColorVariant.color_id);
        setSelectedColorName(urlColorVariant.color_name || '');
        const sizesForColor = product.variants
          .filter((v) => v.color_id === urlColorVariant.color_id && v.stock_quantity > 0)
          .map((v) => v.size)
          .filter((size, index, self) => self.indexOf(size) === index);
        const allSizesForColor = product.variants
          .filter((v) => v.color_id === urlColorVariant.color_id)
          .map((v) => v.size)
          .filter((size, index, self) => self.indexOf(size) === index);
        setSelectedSize(
          sizesForColor.length > 0
            ? sizesForColor[0]
            : allSizesForColor.length > 0
              ? allSizesForColor[0]
              : null
        );
        return;
      }

      // Find first available color with stock
      const colorsWithStock = [
        ...new Set(
          product.variants
            .filter((v) => v.stock_quantity > 0)
            .map((v) => ({id: v.color_id, color_name: v.color_name }))
            .filter(Boolean)
        ),
      ];

      if (colorsWithStock.length > 0) {
        const firstAvailableColor = colorsWithStock[0];
        setSelectedColor(firstAvailableColor.id);
        setSelectedColorName(firstAvailableColor?.color_name || '');
        // Find first available size for this color
        const sizesForColor = product.variants
          .filter((v) => v.color_id === firstAvailableColor.id && v.stock_quantity > 0)
          .map((v) => v.size)
          .filter((size, index, self) => self.indexOf(size) === index);

        if (sizesForColor.length > 0) {
          setSelectedSize(sizesForColor[0]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, preferredColorFromUrl]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${id}`);
      setProduct(response);
      setInWishlist(response.in_wishlist === true);
      // Fetch size chart if product has one
      if (response.size_chart_id) {
        fetchSizeChart(response.size_chart_id);
      }
    } catch (error) {
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const fetchSizeChart = async (sizeChartId) => {
    try {
      setLoadingSizeChart(true);
      const response = await api.get(`/size-charts/${sizeChartId}`);
      setSizeChart(response);
    } catch (error) {
      console.error('Error fetching size chart:', error);
      // Don't show error to user, just don't show size guide
    } finally {
      setLoadingSizeChart(false);
    }
  };

  const handleSizeGuideClick = (e) => {
    e.preventDefault();
    if (product?.size_chart_id && sizeChart) {
      setShowSizeChartModal(true);
    } else if (product?.size_chart_id && !sizeChart && !loadingSizeChart) {
      // Try to fetch if not already loaded
      fetchSizeChart(product.size_chart_id).then(() => {
        setShowSizeChartModal(true);
      });
    } else {
      toast.info('Size guide not available for this product');
    }
  };

  const fetchUserOrders = async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingOrders(true);
      const response = await api.get(`/reviews/product/${id}/my-orders`);
      const orders = response.orders || [];
      setUserOrders(orders);

      // Auto-select if there's only one order (select first item if multiple items exist)
      if (orders.length === 1 && orders[0].items && orders[0].items.length > 0) {
        const order = orders[0];
        const item = order.items[0]; // Select first item
        setReviewForm((prev) => ({
          ...prev,
          order_id: order.id,
          order_item_id: item.id,
          color: item.color_name || item.color_name_full || '',
          size: item.size || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await api.get(`/reviews/product/${id}`);
      const reviewsData = response.reviews || [];
      setReviews(reviewsData);

      // Calculate stats if not provided
      if (response.stats) {
        // Map backend field names to frontend field names
        const stats = response.stats;
        setReviewStats({
          total_reviews: parseInt(stats.total_reviews) || 0,
          average_rating: parseFloat(stats.average_rating) || 0,
          rating_5: parseInt(stats.five_star) || 0,
          rating_4: parseInt(stats.four_star) || 0,
          rating_3: parseInt(stats.three_star) || 0,
          rating_2: parseInt(stats.two_star) || 0,
          rating_1: parseInt(stats.one_star) || 0,
        });
      } else if (reviewsData.length > 0) {
        const totalReviews = reviewsData.length;
        const totalRating = reviewsData.reduce((sum, r) => sum + (parseFloat(r.rating) || 0), 0);
        const averageRating = totalRating / totalReviews;

        // Calculate distribution - use exact integer ratings (ratings should be 1-5 integers)
        const distribution = { rating_5: 0, rating_4: 0, rating_3: 0, rating_2: 0, rating_1: 0 };
        reviewsData.forEach((review) => {
          const rating = parseInt(review.rating) || 0;
          if (rating === 5) distribution.rating_5++;
          else if (rating === 4) distribution.rating_4++;
          else if (rating === 3) distribution.rating_3++;
          else if (rating === 2) distribution.rating_2++;
          else if (rating === 1) distribution.rating_1++;
        });

        setReviewStats({
          total_reviews: totalReviews,
          average_rating: averageRating,
          ...distribution,
        });
      } else {
        setReviewStats({
          total_reviews: 0,
          average_rating: 0,
          rating_5: 0,
          rating_4: 0,
          rating_3: 0,
          rating_2: 0,
          rating_1: 0,
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviewStats({
        total_reviews: 0,
        average_rating: 0,
        rating_5: 0,
        rating_4: 0,
        rating_3: 0,
        rating_2: 0,
        rating_1: 0,
      });
    }
  };
    const handleShowMore = () => {
    navigate('/products');
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };
  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      if (!selectedSize) {
        toast.info('Please select a size');
        return;
      }
      const variant = product.variants?.find(
        (v) => v.color_id === selectedColor && v.size === selectedSize
      );
      if (variant) {
        savePendingAction({ type: 'addToCart', productId: id, variantId: variant.id, quantity });
      } else {
        savePendingAction({ type: 'addToCart', productId: id });
      }
      toast.info('Please log in to add items to your cart');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    if (!selectedSize) {
      toast.error('Please select a size');
      return;
    }

    try {
      // Find variant
      const variant = product.variants?.find(
        (v) => v.color_id === selectedColor && v.size === selectedSize
      );

      if (!variant) {
        toast.error('Selected variant not available');
        return;
      }

      if (variant.stock_quantity < quantity) {
        if (variant.stock_quantity === 0) {
          toast.error('This product is out of stock');
        } else {
          toast.error(`Only ${variant.stock_quantity} item${variant.stock_quantity === 1 ? '' : 's'} left in stock`);
        }
        return;
      }

      await api.post('/cart', {
        product_id: id,
        variant_id: variant.id,
        quantity,
      });

      toast.success('Added to cart');
      // Trigger cart count update in navbar
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to add to cart';
      toast.error(msg);
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      savePendingAction({ type: 'addToWishlist', productId: id });
      toast.info('Please log in to use the wishlist');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    try {
      if (inWishlist) {
        await api.delete(`/wishlist/${id}`);
        setInWishlist(false);
        toast.success('Removed from wishlist');
        // Trigger wishlist count update in navbar
        window.dispatchEvent(new Event('wishlistUpdated'));
      } else {
        await api.post('/wishlist', { product_id: id });
        setInWishlist(true);
        toast.success('Added to wishlist');
        // Trigger wishlist count update in navbar
        window.dispatchEvent(new Event('wishlistUpdated'));
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update wishlist');
    }
  };

  const fetchBestsellers = async () => {
    try {
      setLoadingBestsellers(true);
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    } finally {
      setLoadingBestsellers(false);
    }
  };

  const fetchSimilarProducts = async () => {
    try {
      setLoadingSimilar(true);
      const response = await api.get(`/products/similar/${id}`);
      const products = response.products || [];
      // Keep admin curated display_order when present
      const ordered = [...products].sort((a, b) => {
        const ao = a.related_display_order;
        const bo = b.related_display_order;
        if (ao == null && bo == null) return 0;
        if (ao == null) return 1;
        if (bo == null) return -1;
        return (Number(ao) || 0) - (Number(bo) || 0);
      });
      setSimilarProducts(ordered);
    } catch (error) {
      console.error('Error fetching similar products:', error);
    } finally {
      setLoadingSimilar(false);
    }
  };

  if (loading) return null;

  if (!product) return null;

  const availableColors = [...new Set(product.variants?.map((v) => v.color_id).filter(Boolean))];
  const availableSizes = product.variants
    ?.filter((v) => !selectedColor || v.color_id === selectedColor)
    .map((v) => v.size)
    .filter((size, index, self) => self.indexOf(size) === index);

  const selectedVariant = product.variants?.find(
    (v) => v.color_id === selectedColor && v.size === selectedSize
  );

  // Use final_price from product if available, otherwise calculate
  const basePrice = getProductSellingPrice(product);
  const finalPrice = parseFloat(product.final_price) || 0;
  const taxGst = parseFloat(product.tax_gst) || 0;
  const shippingCharges = parseFloat(product.shipping_charges) || 0;
  const gstAmount =
    finalPrice > 0 ? finalPrice - basePrice - shippingCharges : (basePrice * taxGst) / 100;

  const mainImage =
    displayImages[selectedImageIndex]?.image_url || product.primary_image || '/placeholder.png';
  const categoryName = product.category_name || product.category?.name || '';

  // Mobile: swipe on main image to change image
  const handleImageTouchStart = (e) => {
    if (e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  };
  const handleImageTouchEnd = (e) => {
    if (touchStartX.current == null || touchStartY.current == null || displayImages.length <= 1) return;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchEndX - touchStartX.current;
    const diffY = touchEndY - touchStartY.current;
    const minSwipe = 50;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > minSwipe) {
      if (diffX > 0) {
        setSelectedImageIndex((i) => (i > 0 ? i - 1 : displayImages.length - 1));
      } else {
        setSelectedImageIndex((i) => (i < displayImages.length - 1 ? i + 1 : 0));
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const subcategoryName = product.subcategory_name || '';
  const l3CategoryName = product.l3_category_name || '';
  const breadcrumbCategories = [
    product.category_id && categoryName && { id: product.category_id, name: categoryName, param: 'category' },
    product.subcategory_id && subcategoryName && { id: product.subcategory_id, name: subcategoryName, param: 'subcategory' },
    product.l3_category_id && l3CategoryName && { id: product.l3_category_id, name: l3CategoryName, param: 'l3' },
  ].filter(Boolean);

  return (
    <Container className="py-4 product-detail-page">
      {/* Breadcrumb: Products > Category > Subcategory > L3 (when available) > Product name */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/products" className="text-decoration-none">
              Products
            </Link>
          </li>
          {breadcrumbCategories.map((cat, idx) => (
            <li key={cat.id} className="breadcrumb-item">
              <Link
                to={`/products?${cat.param}=${cat.id}`}
                className="text-decoration-none"
              >
                {cat.name}
              </Link>
            </li>
          ))}
          <li className="breadcrumb-item active" aria-current="page">
            {selectedColorName ? `${product.name} - ${selectedColorName}` : product.name}
          </li>
        </ol>
      </nav>

      <Row>
        <Col md={5}>
          <div className="product-images">
            <div
              className="main-image-wrapper"
              onTouchStart={handleImageTouchStart}
              onTouchEnd={handleImageTouchEnd}
              role="img"
              aria-label={`Product image ${selectedImageIndex + 1} of ${displayImages.length}. Swipe to change.`}
            >
              {mainImageLoading && (
                <div className="product-detail-image-skeleton" aria-hidden="true">
                  <div className="product-detail-image-skeleton-shimmer" />
                </div>
              )}
              <CachedImage
                src={mainImage}
                alt={product.name}
                className="main-product-image"
                onLoad={() => setMainImageLoading(false)}
              />
              {/* Wishlist Icon */}
              <button className="product-detail-wishlist-btn" onClick={handleWishlistToggle}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill={inWishlist ? '#B89354' : 'none'}
                  stroke="black"
                  strokeWidth="2"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
              </button>
              {/* Copy link / Share Icon - shows tick for 3s after copy */}
              <button
                className="product-detail-share-btn"
                onClick={handleCopyLink}
                title={linkCopied ? 'Link copied' : 'Copy link'}
                aria-label={linkCopied ? 'Link copied' : 'Copy link'}
              >
                {linkCopied ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#22c55e"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M19 21H8V7H19M19 5H8C7.46957 5 6.96086 5.21071 6.58579 5.58579C6.21071 5.96086 6 6.46957 6 7V21C6 21.5304 6.21071 22.0391 6.58579 22.4142C6.96086 22.7893 7.46957 23 8 23H19C19.5304 23 20.0391 22.7893 20.4142 22.4142C20.7893 22.0391 21 21.5304 21 21V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5ZM16 1H4C3.46957 1 2.96086 1.21071 2.58579 1.58579C2.21071 1.96086 2 2.46957 2 3V17H4V3H16V1Z"
                      fill="black"
                    />
                  </svg>
                )}
              </button>
            </div>
            {/* Thumbnail Images */}
            {displayImages.length > 1 && (
              <div className="thumbnail-images">
                {displayImages.map((img, idx) => (
                  <div
                    key={img.id ?? `${img.image_url}-${idx}`}
                    className={`thumbnail-item ${selectedImageIndex === idx ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(idx)}
                  >
                    <CachedImage src={img.image_url} alt={`${product.name} ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        <Col md={7}>
          {/* Category */}
          {categoryName && (
            <div className="product-category-badge">{categoryName.toUpperCase()}</div>
          )}

          {/* Product Title */}
          <h1 className="product-detail-title">
            {selectedColorName ? `${product.name} - ${selectedColorName}` : product.name}
          </h1>
          {product.subtitle && (
            <p className="product-detail-subtitle">{product.subtitle}</p>
          )}

          {/* Price */}
          <div className="product-price-section mb-4" style={{ borderBottom: '1px solid #e0e0e0' }}>
            <div className="d-flex align-items-baseline gap-3 flex-wrap">
              <span className="current-price-large">₹{basePrice.toFixed(2)}</span>
              {(() => {
                const onSale = product.is_sale === 1 || product.is_sale === true;
                const discounted = hasActiveProductDiscount(product);
                const strike = parseFloat(product.strike_through_price) || 0;
                if (!(strike > basePrice && basePrice > 0)) return null;
                const offPct = getProductDiscountPercent(product);
                return (
                  <>
                    <span
                      className="original-price-large"
                      style={{ textDecoration: 'line-through', color: '#999' }}
                    >
                      ₹{strike.toFixed(2)}
                    </span>
                    {(discounted || onSale) && offPct > 0 && (
                      <span className="discount-badge-large">({offPct}% OFF)</span>
                    )}
                  </>
                );
              })()}
            </div>
            {/* Price Breakdown
            {finalPrice > 0 && (
              <div className="mt-2 small text-muted">
                <div>Base Price: ₹{basePrice.toFixed(2)}</div>
                {taxGst > 0 && gstAmount > 0 && (
                  <div>+ GST ({taxGst}%): ₹{gstAmount.toFixed(2)}</div>
                )}
                {shippingCharges > 0 && (
                  <div>+ Shipping: ₹{shippingCharges.toFixed(2)}</div>
                )}
                <div className="mt-1 fw-bold text-dark">
                  Final Price: ₹{finalPrice.toFixed(2)}
                </div>
              </div>
            )} */}
          </div>

          {/* Variations (Colors) */}
          {product.variants && product.variants.length > 0 && availableColors.length > 0 && (
            <div className="mb-4">
              <label className="variation-label">Variations: {selectedColorName}</label>
              <div className="color-swatches">
                {product.variants
                  .filter(
                    (v, idx, self) => self.findIndex((x) => x.color_id === v.color_id) === idx
                  )
                  .map((variant) => {
                    // Check if this color has any available stock
                    const colorVariants = product.variants.filter(
                      (v) => v.color_id === variant.color_id
                    );
                    const hasStock = colorVariants.some((v) => v.stock_quantity > 0);
                    const isSelected = selectedColor === variant.color_id;
                    const swatchImage = getColorTaggedImage(product.images, variant.color_id);

                    return (
                      <button
                        key={variant.color_id}
                        type="button"
                        className={`color-swatch ${swatchImage ? 'color-swatch-image' : ''} ${
                          isSelected ? 'active' : ''
                        } ${!hasStock ? 'unavailable' : ''}`}
                        style={
                          swatchImage
                            ? undefined
                            : { backgroundColor: variant.color_hex || '#ccc' }
                        }
                        onClick={() => {
                          if (hasStock) {
                            setSelectedColor(variant.color_id);
                            setSelectedColorName(variant.color_name);
                            setSelectedImageIndex(0);
                            setSelectedSize(null);
                          }
                        }}
                        disabled={!hasStock}
                        title={
                          hasStock ? variant.color_name : `${variant.color_name} - Out of Stock`
                        }
                      >
                        {swatchImage && (
                          <CachedImage
                            src={swatchImage}
                            alt={variant.color_name || 'Color'}
                            className="color-swatch-img"
                          />
                        )}
                        {!hasStock && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            viewBox="0 0 48 48"
                            fill="none"
                          >
                            <path
                              d="M-1 0.000183105L48.3426 49.3428"
                              stroke="#E9E9E9"
                              stroke-width="1.2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-dasharray="6 6"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {availableSizes && availableSizes.length > 0 && (
            <div className="mb-4">
              <label className="size-label">
                Size:{' '}
                {product?.size_chart_id && (
                  <Link to="#" className="size-guide-link" onClick={handleSizeGuideClick}>
                    Size Guide
                  </Link>
                )}
              </label>
              <div className="size-buttons">
                {availableSizes.map((size) => {
                  const variant = product.variants.find(
                    (v) => (!selectedColor || v.color_id === selectedColor) && v.size === size
                  );
                  const inStock = variant && variant.stock_quantity > 0;
                  const isSelected = selectedSize === size;

                  return (
                    <button
                      key={size}
                      className={`size-button ${isSelected ? 'selected' : ''} ${
                        !inStock ? 'out-of-stock' : ''
                      }`}
                      disabled={!inStock}
                      onClick={() => setSelectedSize(size)}
                      style={{ position: 'relative' }}
                    >
                      {size}
                      {!inStock && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="50"
                          height="50"
                          viewBox="0 0 50 50"
                          fill="none"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            pointerEvents: 'none'
                          }}
                        >
                          <path
                            d="M5 5L45 45"
                            stroke="#999"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeDasharray="4 4"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity Selector */}
          <div
            className="quantity-selector !mb-4 pb-4"
            style={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <label className="quantity-label">Quantity:</label>
            <div className="quantity-controls">
              <button
                className="quantity-btn"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
                max={selectedVariant?.stock_quantity || 10}
                className="quantity-input"
              />
              <button
                className="quantity-btn"
                onClick={() => {
                  const maxStock = selectedVariant?.stock_quantity ?? 10;
                  if (quantity >= maxStock) {
                    toast.info(`Only ${maxStock} item${maxStock === 1 ? '' : 's'} left`);
                    return;
                  }
                  setQuantity(quantity + 1);
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3V13M3 8H13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Add to Cart Button */}
          <Button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={!selectedSize || (selectedVariant && selectedVariant.stock_quantity === 0)}
          >
            ADD TO CART
          </Button>

          {/* Shipping Info */}
          <div className="shipping-info mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
            >
              <path
                d="M8.66732 25.3333H6.00065C5.63265 25.3333 5.33398 25.0347 5.33398 24.6667C5.33398 24.2987 5.63265 24 6.00065 24H8.66732C9.03532 24 9.33398 24.2987 9.33398 24.6667C9.33398 25.0347 9.03532 25.3333 8.66732 25.3333Z"
                fill="#1F1F1F"
              />
              <path
                d="M30.3321 25.334H28.6654C28.2974 25.334 27.9988 25.0353 27.9988 24.6673C27.9988 24.2993 28.2974 24.0007 28.6654 24.0007H29.7788L30.6774 19.2113C30.6654 16.7607 28.5721 14.6673 25.9988 14.6673H21.6214L19.5001 24.0007H23.3321C23.7001 24.0007 23.9988 24.2993 23.9988 24.6673C23.9988 25.0353 23.7001 25.334 23.3321 25.334H18.6654C18.4628 25.334 18.2708 25.242 18.1441 25.0833C18.0174 24.926 17.9694 24.718 18.0148 24.5207L20.4388 13.854C20.5081 13.5487 20.7774 13.334 21.0894 13.334H25.9988C29.3068 13.334 31.9988 16.026 31.9988 19.334L30.9868 24.79C30.9281 25.106 30.6534 25.334 30.3321 25.334Z"
                fill="#1F1F1F"
              />
              <path
                d="M25.9993 28.0006C24.162 28.0006 22.666 26.506 22.666 24.6673C22.666 22.8286 24.162 21.334 25.9993 21.334C27.8367 21.334 29.3327 22.8286 29.3327 24.6673C29.3327 26.506 27.8367 28.0006 25.9993 28.0006ZM25.9993 22.6673C24.8967 22.6673 23.9993 23.5646 23.9993 24.6673C23.9993 25.77 24.8967 26.6673 25.9993 26.6673C27.102 26.6673 27.9993 25.77 27.9993 24.6673C27.9993 23.5646 27.102 22.6673 25.9993 22.6673Z"
                fill="#1F1F1F"
              />
              <path
                d="M11.3333 28.0006C9.496 28.0006 8 26.506 8 24.6673C8 22.8286 9.496 21.334 11.3333 21.334C13.1707 21.334 14.6667 22.8286 14.6667 24.6673C14.6667 26.506 13.1707 28.0006 11.3333 28.0006ZM11.3333 22.6673C10.2307 22.6673 9.33333 23.5646 9.33333 24.6673C9.33333 25.77 10.2307 26.6673 11.3333 26.6673C12.436 26.6673 13.3333 25.77 13.3333 24.6673C13.3333 23.5646 12.436 22.6673 11.3333 22.6673Z"
                fill="#1F1F1F"
              />
              <path
                d="M8.66602 13.3333H3.33268C2.96468 13.3333 2.66602 13.0347 2.66602 12.6667C2.66602 12.2987 2.96468 12 3.33268 12H8.66602C9.03402 12 9.33268 12.2987 9.33268 12.6667C9.33268 13.0347 9.03402 13.3333 8.66602 13.3333Z"
                fill="#1F1F1F"
              />
              <path
                d="M8.66732 17.3333H2.00065C1.63265 17.3333 1.33398 17.0347 1.33398 16.6667C1.33398 16.2987 1.63265 16 2.00065 16H8.66732C9.03532 16 9.33398 16.2987 9.33398 16.6667C9.33398 17.0347 9.03532 17.3333 8.66732 17.3333Z"
                fill="#1F1F1F"
              />
              <path
                d="M8.66667 21.3333H0.666667C0.298667 21.3333 0 21.0347 0 20.6667C0 20.2987 0.298667 20 0.666667 20H8.66667C9.03467 20 9.33333 20.2987 9.33333 20.6667C9.33333 21.0347 9.03467 21.3333 8.66667 21.3333Z"
                fill="#1F1F1F"
              />
              <path
                d="M18.6673 25.334H14.0007C13.6327 25.334 13.334 25.0353 13.334 24.6673C13.334 24.2993 13.6327 24.0007 14.0007 24.0007H18.1353L21.1647 10.6673H6.00065C5.63265 10.6673 5.33398 10.3687 5.33398 10.0007C5.33398 9.63265 5.63265 9.33398 6.00065 9.33398H22.0007C22.2033 9.33398 22.3953 9.42598 22.522 9.58465C22.6487 9.74198 22.6967 9.94998 22.6513 10.1473L19.318 24.814C19.2487 25.1193 18.978 25.334 18.6673 25.334Z"
                fill="#1F1F1F"
              />
            </svg>
            {(() => {
              // Check if any variant has stock
              const hasAnyStock = product.variants?.some((v) => v.stock_quantity > 0) || false;
              return hasAnyStock ? (
                <span className="stock-status">In Stock - Available For Immediate Shipping</span>
              ) : (
                <span className="stock-status out-of-stock-message">Currently Out of Stock</span>
              );
            })()}
          </div>

          {/* Product Details And Care - Expandable */}
          <div className="product-details-section">
            <button className="details-toggle-btn" onClick={() => setShowDetails(!showDetails)}>
              <span>Product Details And Care</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className={showDetails ? 'rotated' : ''}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {showDetails && (
              <div className="details-content">
                {/* Fit */}
                {(product.fit || product.product_type) && (
                  <div className="detail-subsection">
                    <h6>Fit</h6>
                    <p>
                      {product.fit
                        ? product.fit
                        : `Designed with a comfortable regular fit, this ${(product.product_type || '').toLowerCase()} offers ease of movement without feeling loose. It sits neatly on the shoulders and drapes naturally for a clean, relaxed look.`}
                    </p>
                  </div>
                )}

                {/* Features */}
                <div className="detail-subsection">
                  <h6>Features</h6>
                  <ul>
                    {(() => {
                      let featList = product.features;
                      if (typeof featList === 'string') {
                        try { featList = JSON.parse(featList); } catch { featList = []; }
                      }
                      const hasList = Array.isArray(featList) && featList.length > 0;
                      if (hasList) {
                        return featList.map((item, i) => (
                          <li key={i}>{typeof item === 'string' ? item : String(item)}</li>
                        ));
                      }
                      return (
                        <>
                          {product.fabric_type && (
                            <li>Soft, breathable {product.fabric_type.toLowerCase()} fabric</li>
                          )}
                          {product.pattern && (
                            <li>Distinctive {product.pattern.toLowerCase()} pattern</li>
                          )}
                          <li>Full button-front closure</li>
                          {product.texture && <li>Unique {product.texture.toLowerCase()} texture</li>}
                          <li>Lightweight inner finish for all-day comfort</li>
                        </>
                      );
                    })()}
                  </ul>
                </div>

               

                {/* Material And Care */}
                <div className="detail-subsection">
                  <h6>Material And Care</h6>
                  {product.fabric_type && (
                    <p>
                      <strong>Fabric:</strong> {product.fabric_type}
                    </p>
                  )}
                   {/* Product details: type, occasion, pattern, texture */}
                {(product.product_type || product.occasion || product.pattern || product.texture) && (
                  <div className="detail-subsection">

                   
                      {product.product_type && (
                        <p><strong>Product type:</strong> {product.product_type}</p>
                      )}
                      {product.occasion && (
                        <p><strong>Occasion:</strong> {product.occasion}</p>
                      )}
                      {product.pattern && (
                        <p><strong>Pattern:</strong> {product.pattern}</p>
                      )}
                      {product.texture && (
                        <p><strong>Texture:</strong> {product.texture}</p>
                      )}
                
                  </div>
                )}
                  {product.care_instructions && (
                    <p>
                      <strong>Care Instructions:</strong> {product.care_instructions}
                    </p>
                  )}
                  {product.safety_instructions && (
                    <p>
                      <strong>Safety Instructions:</strong> {product.safety_instructions}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Delivery Section */}
          <div className="delivery-section">
            <button className="section-toggle-btn" onClick={() => setShowDelivery(!showDelivery)}>
              <span>Delivery</span>
              <div className="delivery-header-right">
               
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={showDelivery ? 'rotated' : ''}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
            {showDelivery && (
              <div className="delivery-content">
                <p>Orders within Tamil Nadu will be delivered within 2 working days.</p>
                <p>Orders outside Tamil Nadu will be delivered within 3 - 4 working days.</p>
              </div>
            )}
          </div>

          {/* Return & Replacement */}
          <div className="return-replacement-section mt-3 mb-3 p-3 border rounded" style={{ borderColor: '#e0e0e0' }}>
            <h6 className="mb-2" style={{ fontWeight: 600, color: '#333' }}>Return & Replacement</h6>
            {(product.returnable === true || product.returnable === 1) || (product.replacement_allowed === true || product.replacement_allowed === 1) ? (
              <div className="d-flex flex-wrap gap-2">
                {(product.returnable === true || product.returnable === 1) && (
                  <span className="badge bg-success" style={{ fontSize: '0.85rem' }}>Returnable</span>
                )}
                {(product.replacement_allowed === true || product.replacement_allowed === 1) && (
                  <span className="badge border border-[#e0e0e0] text-dark" style={{ fontSize: '0.85rem' }}>Replacement allowed</span>
                )}
              </div>
            ) : (
              <p className="text-muted mb-0 small">This item cannot be returned.</p>
            )}
          </div>

          {/* Reviews / Comments Section (stars/ratings overview removed) */}
          <div className="reviews-ratings-section">
            {/* Write Review Button - only if user has order history for this product (non-returned) */}
            <div className="write-review-container">
              {!isAuthenticated && (
                <Button
                  className="write-review-btn"
                  onClick={() => {
                    toast.info('Please log in to write a review');
                    setTimeout(() => navigate('/login'), 400);
                  }}
                >
                  WRITE A REVIEW
                </Button>
              )}
              {isAuthenticated && userOrders.length > 0 && (
                <Button
                  className="write-review-btn"
                  onClick={() => {
                    setReviewForm({
                      rating: 5,
                      review_text: '',
                      order_id: null,
                      order_item_id: null,
                      color: '',
                      size: '',
                    });
                    setEditingReview(null);
                    fetchUserOrders();
                    setShowReviewModal(true);
                  }}
                >
                  WRITE A REVIEW
                </Button>
              )}
              {isAuthenticated && userOrders.length === 0 && !loadingOrders && (
                <p className="text-muted small mb-0">You can review this product after you order it.</p>
              )}
              {isAuthenticated && loadingOrders && (
                <p className="text-muted small mb-0">Loading...</p>
              )}
            </div>
          </div>

          {/* Comments Section */}
          <div className="comments-section">
            <div className="comments-header">
              <h3 className="section-title">Comments</h3>
              <div className="sort-dropdown">
                <label>Sort By:</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>
            </div>

            <div className="comments-list">
              {(() => {
                let sortedReviews = [...reviews];

                // Sort reviews based on sortBy
                if (sortBy === 'newest') {
                  sortedReviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                } else if (sortBy === 'oldest') {
                  sortedReviews.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                }

                const getTimeAgo = (date) => {
                  const now = new Date();
                  const reviewDate = new Date(date);
                  const diffInSeconds = Math.floor((now - reviewDate) / 1000);

                  if (diffInSeconds < 60) return 'Just now';
                  const diffInMinutes = Math.floor(diffInSeconds / 60);
                  if (diffInMinutes < 60)
                    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
                  const diffInHours = Math.floor(diffInMinutes / 60);
                  if (diffInHours < 24)
                    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
                  const diffInDays = Math.floor(diffInHours / 24);
                  if (diffInDays < 7)
                    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
                  const diffInWeeks = Math.floor(diffInDays / 7);
                  if (diffInWeeks < 4)
                    return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
                  const diffInMonths = Math.floor(diffInDays / 30);
                  return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
                };

                return sortedReviews.length > 0 ? (
                  sortedReviews.map((review) => (
                    <div key={review.id} className="comment-item">
                      <div className="comment-header">
                        <div className="comment-user">
                          <div className="user-avatar" />
                          <div>
                            <div className="user-name flex gap-4">
                              {review.user_name || 'Anonymous'}
                            </div>
                            <div className="comment-meta">
                              <span className="comment-date">{getTimeAgo(review.created_at)}</span>
                              {(review.color_name || review.size) && (
                                <span className="comment-variant">
                                  {review.color_name && review.size
                                    ? `${review.color_name} / ${review.size}`
                                    : review.color_name || review.size}
                                </span>
                              )}
                              {review.variant_info && !review.color_name && !review.size && (
                                <span className="comment-variant">{review.variant_info}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {user && review.user_id === user.id && (
                          <div className="comment-options-wrapper">
                            <button
                              className="comment-options"
                              onClick={() =>
                                setShowCommentMenu(showCommentMenu === review.id ? null : review.id)
                              }
                            >
                              ⋯
                            </button>
                            {showCommentMenu === review.id && (
                              <div className="comment-menu-dropdown">
                                <button
                                  onClick={() => {
                                    setEditingReview(review);
                                    setShowCommentMenu(null);
                                    setReviewForm({
                                      rating: review.rating,
                                      review_text: review.review_text || '',
                                      order_id: review.order_id,
                                      order_item_id: null,
                                      color: review.color_name || '',
                                      size: review.size || '',
                                    });
                                    setShowReviewModal(true);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={async () => {
                                    if (
                                      window.confirm('Are you sure you want to delete this review?')
                                    ) {
                                      try {
                                        await api.delete(`/reviews/${review.id}`);
                                        toast.success('Review deleted successfully');
                                        fetchReviews();
                                        setShowCommentMenu(null);
                                      } catch (error) {
                                        toast.error(
                                          error.response?.data?.error || 'Failed to delete review'
                                        );
                                      }
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="comment-text">
                        {review.review_text || 'No comment provided.'}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="no-comments">No comments yet. Be the first to comment!</p>
                );
              })()}
            </div>
          </div>

          {/* Leave A Comment Section */}
          {/* <div className="leave-comment-section">
            <h3 className="section-title">Leave A Comment</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Handle comment submission
                toast.info('Comment submission functionality to be implemented');
              }}
            >
              <div className="comment-form-row">
                <input
                  type="text"
                  placeholder="Your Name*"
                  required
                  value={commentForm.name}
                  onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                />
                <input
                  type="email"
                  placeholder="Your Email*"
                  required
                  value={commentForm.email}
                  onChange={(e) => setCommentForm({ ...commentForm, email: e.target.value })}
                />
              </div>
              <textarea
                placeholder="Your Message*"
                required
                rows={5}
                value={commentForm.message}
                onChange={(e) => setCommentForm({ ...commentForm, message: e.target.value })}
              />
              <label className="save-info-checkbox">
                <input
                  type="checkbox"
                  checked={commentForm.saveInfo}
                  onChange={(e) => setCommentForm({ ...commentForm, saveInfo: e.target.checked })}
                />
                <span>
                  Save my name, email, and website in this browser for the next time I comment.
                </span>
              </label>
              <Button type="submit" className="submit-comment-btn">
                SUBMIT COMMENT
              </Button>
            </form>
          </div> */}
        </Col>
      </Row>

      {/* Write Review Modal */}
      {showReviewModal && (
        <div className="review-modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h3>{editingReview ? 'Edit Review' : 'Write A Review'}</h3>
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setEditingReview(null);
                  setReviewForm({
                    rating: 5,
                    review_text: '',
                    order_id: null,
                    order_item_id: null,
                    color: '',
                    size: '',
                  });
                }}
              >
                ×
              </button>
            </div>
            <div className="review-modal-body">
              <p>
                <strong>Product:</strong> {product.name}
              </p>
              {isAuthenticated && userOrders.length > 0 && !editingReview && (
                <div className="review-order-selector">
                  <label>Select Order & Variant</label>
                  <select
                    className="form-select"
                    value={
                      reviewForm.order_id && reviewForm.order_item_id
                        ? `${reviewForm.order_id}-${reviewForm.order_item_id}`
                        : ''
                    }
                    onChange={(e) => {
                      const [orderId, itemId] = e.target.value.split('-');
                      const order = userOrders.find((o) => o.id === parseInt(orderId));
                      const item = order?.items?.find((i) => i.id === parseInt(itemId));
                      setReviewForm({
                        ...reviewForm,
                        order_id: parseInt(orderId),
                        order_item_id: parseInt(itemId),
                        color: item?.color_name || item?.color_name_full || '',
                        size: item?.size || '',
                      });
                    }}
                  >
                    <option value="">Select order and variant...</option>
                    {userOrders.map((order) =>
                      order.items?.map((item) => (
                        <option key={`${order.id}-${item.id}`} value={`${order.id}-${item.id}`}>
                          Order #{order.order_number} -{' '}
                          {item.color_name || item.color_name_full || 'N/A'} / {item.size || 'N/A'}
                        </option>
                      ))
                    )}
                  </select>
                  {reviewForm.color && reviewForm.size && (
                    <div className="selected-variant-info">
                      <strong>Color:</strong> {reviewForm.color} | <strong>Size:</strong>{' '}
                      {reviewForm.size}
                    </div>
                  )}
                </div>
              )}
              {loadingOrders && <p>Loading your orders...</p>}
              {(!userOrders || userOrders.length === 0) && isAuthenticated && !editingReview && (
                <div className="review-variant-fields">
                  <div className="review-field-row">
                    <div className="review-field">
                      <label>Color (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., Red, Blue"
                        value={reviewForm.color}
                        onChange={(e) => setReviewForm({ ...reviewForm, color: e.target.value })}
                      />
                    </div>
                    <div className="review-field">
                      <label>Size (Optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., M, L, XL"
                        value={reviewForm.size}
                        onChange={(e) => setReviewForm({ ...reviewForm, size: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
              {editingReview && (
                <div className="review-variant-fields">
                  <div className="review-field-row">
                    <div className="review-field">
                      <label>Color</label>
                      <input
                        type="text"
                        placeholder="e.g., Red, Blue"
                        value={reviewForm.color || ''}
                        disabled
                        readOnly
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                    <div className="review-field">
                      <label>Size</label>
                      <input
                        type="text"
                        placeholder="e.g., M, L, XL"
                        value={reviewForm.size || ''}
                        disabled
                        readOnly
                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* Rating stars hidden — comments only
              <div className="review-rating-selector">
                <label>Rating</label>
                <div className="rating-buttons">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`rating-star-btn ${star <= reviewForm.rating ? 'active' : ''}`}
                      onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>
              */}
              <div className="review-text-area">
                <label>Comment</label>
                <textarea
                  rows={4}
                  placeholder="Share your experience with this product..."
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                />
              </div>
            </div>
            <div className="review-modal-footer">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowReviewModal(false);
                  setEditingReview(null);
                  setReviewForm({
                    rating: 5,
                    review_text: '',
                    order_id: null,
                    order_item_id: null,
                    color: '',
                    size: '',
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                className="bg-primary border-0"
                onClick={async () => {
                  if (!isAuthenticated) {
                    toast.info('Please log in to submit a review');
                    setTimeout(() => navigate('/login'), 400);
                    return;
                  }
                  try {
                    if (editingReview) {
                      await api.put(`/reviews/${editingReview.id}`, {
                        rating: reviewForm.rating,
                        review_text: reviewForm.review_text,
                        color_name: reviewForm.color || null,
                        size: reviewForm.size || null,
                      });
                      toast.success('Review updated successfully');
                    } else {
                      await api.post('/reviews', {
                        product_id: id,
                        order_id: reviewForm.order_id,
                        rating: reviewForm.rating,
                        review_text: reviewForm.review_text,
                        color_name: reviewForm.color || null,
                        size: reviewForm.size || null,
                      });
                      toast.success('Review submitted successfully');
                    }
                    setShowReviewModal(false);
                    setEditingReview(null);
                    setReviewForm({
                      rating: 5,
                      review_text: '',
                      order_id: null,
                      order_item_id: null,
                      color: '',
                      size: '',
                    });
                    fetchReviews();
                  } catch (error) {
                    toast.error(error.response?.data?.error || 'Failed to submit review');
                  }
                }}
              >
                {editingReview ? 'Update Review' : 'Submit Review'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Size Chart Modal */}
      {showSizeChartModal && (
        <div className="review-modal-overlay" onClick={() => setShowSizeChartModal(false)}>
          <div className="review-modal size-chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h3>{sizeChart?.name || 'Size Guide'}</h3>
              <button onClick={() => setShowSizeChartModal(false)}>×</button>
            </div>
            <div className="review-modal-body">
              {loadingSizeChart ? (
                <div className="text-center py-4">
                  <p>Loading size guide...</p>
                </div>
              ) : sizeChart && sizeChart.chart_data ? (
                <div className="size-chart-modal-content">
                  {(() => {
                    let chartData = sizeChart.chart_data;
                    if (typeof chartData === 'string') {
                      chartData = JSON.parse(chartData);
                    }

                    if (!chartData.headers || !chartData.rows) {
                      return <p>Size chart data is not available.</p>;
                    }

                    return (
                      <div className="table-responsive">
                        <table className="table table-bordered size-chart-table">
                          <thead>
                            <tr>
                              {chartData.headers.map((header, index) => (
                                <th key={index} className="text-center fw-bold">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {chartData.rows.map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, colIndex) => (
                                  <td key={colIndex} className="text-center">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <p>Size guide is not available for this product.</p>
              )}
            </div>
          
          </div>
        </div>
      )}

      {/* Suggestions Section (admin-curated related products, with category fallback) */}
      {similarProducts.length > 0 && (
        <div className="similar-products-section suggestions-section mt-5">
          <h2 className="section-title mb-4">Suggestions</h2>
          <div className="suggestions-grid">
            {similarProducts.map((product) => (
              <div key={product.id} className="suggestions-grid-item">
                <ProductCard product={product} buttonText="Add to Cart" />
              </div>
            ))}
          </div>
          <div className="w-100 flex align-items-center justify-content-center">
            <button onClick={handleShowMore} className="load-more-btn my-4">
              Show More
            </button>
          </div>
        </div>
      )}

      {/* Bestsellers Section */}
      {bestsellers.length > 0 && (
        <div className="bestsellers-section mt-5">
          <h2 className="section-title mb-4">Bestsellers</h2>
          <Row>
            {bestsellers.map((product) => (
              <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                <ProductCard product={product} buttonText="Add to Cart" />
              </Col>
            ))}
          </Row>
          <div className="w-100 flex align-items-center justify-content-center">

      <button onClick={handleShowMore} className="load-more-btn my-4">Show More</button>
      </div>
        </div>
      )}
    </Container>
  );
};

export default ProductDetail;
